import { Navigate, useLocation } from "react-router-dom";
import { getAccessToken } from "@/api/client";
import { useAuthStore } from "@/stores/auth";
import { NDASigningGate } from "@/components/NDASigningGate";
import { OnboardingGate } from "@/components/OnboardingGate";
import { ActivationGate } from "@/components/activation/ActivationGate";
import type { ReactNode } from "react";

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRoles?: string[];
}

export function ProtectedRoute({ children, requiredRoles }: ProtectedRouteProps) {
  const location = useLocation();
  const token = getAccessToken();
  const user = useAuthStore((s) => s.user);

  if (!token) {
    // Preserve where they were headed as a `?next=` query param — Login reads that (its
    // router `state` was never consumed). This matters for redirect targets whose query
    // string cannot be reconstructed after login, e.g. GitHub's post-install bounce to
    // /settings/integrations?installation_id=… — losing it silently leaves the org
    // unconnected. postLoginDest() whitelists to internal paths, so this is open-redirect safe.
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }

  if (requiredRoles && requiredRoles.length > 0 && user) {
    const userRoles = (user.roles ?? []).map((r) => r.toLowerCase());
    // ORG_ADMIN is a super-admin — always passes role checks
    const isAdmin = userRoles.includes("org_admin");
    const hasRole = isAdmin || requiredRoles.some(
      (r) => userRoles.includes(r.toLowerCase())
    );
    if (!hasRole) {
      return <Navigate to="/" replace />;
    }
  }

  // A meeting is never held behind first-run onboarding (register row 35). A
  // member who had not finished it opened the meeting link and got Felix's
  // welcome screen instead of the lobby. Onboarding still runs on their next
  // visit to any other page.
  const inMeeting = location.pathname.startsWith("/meeting/");
  const gated = <ActivationGate>{children}</ActivationGate>;
  return (
    <NDASigningGate>
      {inMeeting ? gated : <OnboardingGate>{gated}</OnboardingGate>}
    </NDASigningGate>
  );
}
